import React, { useState } from "react";
import {
  Box,
  InputLabel,
  Select,
  Typography,
  Grid,
  SelectChangeEvent,
  Link,
  Dialog,
  DialogContent,
  IconButton,
  List,
  ListItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { AuthenticationContext } from "../Context";

interface Props {
  renderingProviderId: string;
  onChange: (field: string, value: string) => void;
  renderingProviders: any[];
  claimStatus: string;
}

const RenderingProvider: React.FC<Props> = ({
  renderingProviderId,
  onChange,
  renderingProviders,
  claimStatus,
}) => {
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const authenticationSetting = React.useContext(AuthenticationContext);
  const currentUser = authenticationSetting?.currentUser;

  const isCurrentUserQualified = React.useMemo(() => {
    return !!(
      currentUser?.provider_npi_number &&
      currentUser?.id_qualifier &&
      currentUser?.id_number
    );
  }, [currentUser]);

  const availableProviders = React.useMemo(() => {
    const providers = renderingProviders || [];

    if (
      isCurrentUserQualified &&
      currentUser &&
      !providers.some((p) => String(p.id) === String(currentUser.id))
    ) {
      return [currentUser, ...providers];
    }

    return providers;
  }, [renderingProviders, isCurrentUserQualified, currentUser]);

  const selectedProvider = React.useMemo(() => {
    if (!renderingProviderId) return null;
    return availableProviders.find(
      (p) => String(p.id) === String(renderingProviderId)
    );
  }, [renderingProviderId, availableProviders]);

  const isUserSelected = React.useMemo(() => {
    return (
      renderingProviderId &&
      currentUser &&
      String(renderingProviderId) === String(currentUser.id)
    );
  }, [renderingProviderId, currentUser]);

  const isAnotherProviderSelected = React.useMemo(() => {
    return renderingProviderId && !isUserSelected && selectedProvider !== null;
  }, [renderingProviderId, isUserSelected, selectedProvider]);

  const getMessageText = (): string => {
    if (isAnotherProviderSelected && selectedProvider) {
      return `${selectedProvider.name} is now the rendering provider for the charge line.`;
    }
    if (isCurrentUserQualified) {
      return "You are the rendering provider! However, if you're working with another provider, you may select them at your discretion.";
    }
    return "You are not qualified to be a rendering provider. Please select a different provider.";
  };

  const handleProviderChange = (event: SelectChangeEvent<string>) => {
    const selectedProviderId = event.target.value;
    onChange("rendering_provider_id", selectedProviderId);

    const selectedProvider = availableProviders.find(
      (p) => String(p.id) === String(selectedProviderId)
    );
    if (selectedProvider) {
      onChange("ren_provider_qual", selectedProvider.id_qualifier || "");
      onChange("ren_provider_npi", selectedProvider.provider_npi_number || "");
      onChange("ren_provider_tax", selectedProvider.id_number || "");
    } else {
      onChange("ren_provider_qual", "");
      onChange("ren_provider_npi", "");
      onChange("ren_provider_tax", "");
    }
  };

  const handleHelpClick = (event: React.MouseEvent) => {
    event.preventDefault();
    setHelpDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setHelpDialogOpen(false);
  };

  React.useEffect(() => {
    if (
      isCurrentUserQualified &&
      currentUser &&
      !renderingProviderId &&
      availableProviders.some((p) => String(p.id) === String(currentUser.id))
    ) {
      onChange("rendering_provider_id", String(currentUser.id));
      onChange("ren_provider_qual", currentUser.id_qualifier || "");
      onChange("ren_provider_npi", currentUser.provider_npi_number || "");
      onChange("ren_provider_tax", currentUser.id_number || "");
    }
  }, [
    isCurrentUserQualified,
    currentUser,
    renderingProviderId,
    availableProviders,
    onChange,
  ]);

  const getDefaultOptionValue = (): string => {
    if (isCurrentUserQualified && currentUser) {
      return String(currentUser.id);
    }
    return "";
  };

  const selectValue = renderingProviderId || getDefaultOptionValue();

  return (
    <Grid container spacing={2} alignItems="center" sx={{ minHeight: "160px" }}>
      <Grid item xs={6.5}>
        <Box>
          <Typography
            sx={{
              color: "#A29D9B",
              fontSize: "12px",
              lineHeight: "26px",
              fontWeight: 700,
            }}
          >
            Key Details
          </Typography>
          <p
            style={{
              color: "#707070",
              fontSize: "10px",
              lineHeight: "14px",
              fontWeight: 400,
            }}
          >
            {getMessageText()}
          </p>
        </Box>
      </Grid>

      <Grid item xs={5.5}>
        <Box>
          <InputLabel
            htmlFor="rendering_provider"
            sx={{
              color: "#A29D9B",
              fontWeight: 700,
              fontSize: "12px",
              lineHeight: "26px",
            }}
          >
            Rendering Provider*
          </InputLabel>
          <Box className="field-container" sx={{ marginLeft: "0px" }}>
            <Select
              id="rendering_provider"
              value={selectValue}
              required
              variant="outlined"
              className="the-field"
              native
              disabled={
                claimStatus === "submitted" ||
                claimStatus === "pending_submission"
              }
              onChange={handleProviderChange}
            >
              {!isCurrentUserQualified && (
                <option value="">Select Provider</option>
              )}
              {availableProviders?.map((provider) => (
                <option key={provider.id} value={provider.id}>
                  {provider.name}
                </option>
              ))}
            </Select>
          </Box>
          <Box sx={{ mt: 1 }}>
            <Link
              component="button"
              variant="body2"
              onClick={handleHelpClick}
              sx={{
                fontSize: "10px !important",
                lineHeight: "14px !important",
                textDecoration: "underline !important",
                color: "#3F51B5 !important",
                textAlign: "left",
              }}
            >
              How do I qualify as a rendering provider?
            </Link>
          </Box>
        </Box>
      </Grid>

      <Dialog
        open={helpDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "25px",
            borderBottom: "2px solid #FF890A",
            backgroundColor: "#FFFFFF",
          }}
        >
          <Typography
            sx={{
              fontSize: "21px",
              fontWeight: 700,
              lineHeight: "26px",
              color: "#1E1E1E",
            }}
          >
            Rendering Provider Eligibility
          </Typography>
          <IconButton
            onClick={handleCloseDialog}
            sx={{
              "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
              marginLeft: "auto",
              padding: "0px",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: "25px" }}>
          <Typography
            variant="body1"
            sx={{
              fontSize: "14px",
              lineHeight: "22px",
              color: "#1E1E1E",
            }}
          >
            To be eligible as a rendering provider, a user must meet all of the
            following criteria:
          </Typography>
          <Box>
            <List sx={{ listStyleType: "disc", pl: 3 }}>
              <ListItem sx={{ display: "list-item", pl: 0, mb: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "14px",
                    lineHeight: "22px",
                    color: "#1E1E1E",
                  }}
                >
                  <b>Valid NPI:</b> The provider must have an active National
                  Provider Identifier (NPI) registered in the system.
                </Typography>
              </ListItem>

              <ListItem sx={{ display: "list-item", pl: 0, mb: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "14px",
                    lineHeight: "22px",
                    color: "#1E1E1E",
                  }}
                >
                  <b>ID Verification:</b> The provider must have a verified
                  internal ID in the system.
                </Typography>
              </ListItem>

              <ListItem sx={{ display: "list-item", pl: 0, mb: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "14px",
                    lineHeight: "22px",
                    color: "#1E1E1E",
                  }}
                >
                  <b>Rendering Number:</b> The provider must have a valid
                  Rendering Number assigned.
                </Typography>
              </ListItem>
            </List>
          </Box>
          <Typography
            variant="body2"
            sx={{
              fontSize: "14px",
              lineHeight: "22px",
              color: "#1E1E1E",
            }}
          >
            If you do not meet all of these requirements, your name will not
            appear in the dropdown. In that case, please select another eligible
            provider from the list.
          </Typography>
        </DialogContent>
      </Dialog>
    </Grid>
  );
};

export default RenderingProvider;
